import React from "react";
import ArticleLayout, { Step, Tip, CodeBlock, Check, Ext } from "./ArticleLayout";

export default function MetaChannelsGuide() {
  return (
    <ArticleLayout
      kicker="How-to guide"
      title="Connect Facebook Messenger, Instagram & WhatsApp Cloud."
      intro="Three Meta-owned channels share one setup. Do steps 1–5 once, then enable each channel you want."
    >
      <Step n="1" title="Have a Facebook Page (and optionally an Instagram business account)">
        <p>Messenger needs a <strong className="text-[#F5F2EB]">Facebook Page</strong> for your business. Instagram DMs need an <strong className="text-[#F5F2EB]">Instagram Business or Creator account</strong> linked to that Page.</p>
        <p>If you don't have these yet, set them up at <Ext href="https://business.facebook.com">business.facebook.com</Ext> first — takes ~10 minutes.</p>
      </Step>

      <Step n="2" title="Create a Meta App">
        <p>Go to <Ext href="https://developers.facebook.com/apps/">developers.facebook.com/apps</Ext> → <strong className="text-[#F5F2EB]">Create app</strong>.</p>
        <ul className="list-none space-y-1 pl-1">
          <Check>App type: <strong className="text-[#F5F2EB]">Business</strong></Check>
          <Check>App name: anything (e.g. "My Restaurant Agent")</Check>
          <Check>Contact email: yours</Check>
          <Check>Business account: pick yours from the dropdown</Check>
        </ul>
        <p>Once created, you land on the App Dashboard.</p>
      </Step>

      <Step n="3" title="Add the products you want">
        <p>In the App Dashboard sidebar, find <strong className="text-[#F5F2EB]">Add product</strong>. Click <strong className="text-[#F5F2EB]">Set up</strong> next to each one you want:</p>
        <ul className="list-none space-y-1 pl-1">
          <Check><strong className="text-[#F5F2EB]">Messenger</strong> — for Facebook Page DMs</Check>
          <Check><strong className="text-[#F5F2EB]">Instagram</strong> → Instagram messaging — for IG DMs</Check>
          <Check><strong className="text-[#F5F2EB]">WhatsApp</strong> — for WhatsApp Business via Meta Cloud API</Check>
        </ul>
      </Step>

      <Step n="4" title="Grab your App Secret">
        <p>App Dashboard → <strong className="text-[#F5F2EB]">App settings → Basic</strong>. Find <strong className="text-[#F5F2EB]">App Secret</strong>, click <strong className="text-[#F5F2EB]">Show</strong>, copy it.</p>
        <Tip>This secret signs every webhook request — keep it private and never paste it in chat.</Tip>
      </Step>

      <Step n="5" title="Paste webhook URL + verify token from AgentReply">
        <p>Back in AgentReply: open your agent → <strong className="text-[#F5F2EB]">Meta channels</strong> tab. You'll see two grey boxes labelled <strong className="text-[#F5F2EB]">Webhook URL</strong> and <strong className="text-[#F5F2EB]">Verify token</strong>.</p>
        <p>Now go back to Meta App Dashboard → the product you're setting up → <strong className="text-[#F5F2EB]">Webhooks</strong> → <strong className="text-[#F5F2EB]">Configure</strong>. Paste:</p>
        <ul className="list-none space-y-1 pl-1">
          <Check><strong className="text-[#F5F2EB]">Callback URL</strong> = the Webhook URL we showed you</Check>
          <Check><strong className="text-[#F5F2EB]">Verify Token</strong> = the Verify Token we showed you</Check>
        </ul>
        <p>Click <strong className="text-[#F5F2EB]">Verify and save</strong>. Meta sends a test ping → we echo the challenge → green check ✅.</p>
      </Step>

      <Step n="6" title="Subscribe to the right webhook fields">
        <p>Still in the Webhooks page, you'll see a list of subscription fields. Tick:</p>
        <ul className="list-none space-y-1 pl-1">
          <Check><strong className="text-[#F5F2EB]">Messenger</strong>: <code>messages</code>, <code>messaging_postbacks</code></Check>
          <Check><strong className="text-[#F5F2EB]">Instagram</strong>: <code>messages</code>, <code>messaging_postbacks</code></Check>
          <Check><strong className="text-[#F5F2EB]">WhatsApp</strong>: <code>messages</code></Check>
        </ul>
      </Step>

      <Step n="7" title="Generate a Page Access Token (Messenger / Instagram)">
        <p>Messenger product page → <strong className="text-[#F5F2EB]">Access tokens</strong> section → pick your Page from the dropdown → <strong className="text-[#F5F2EB]">Generate token</strong>.</p>
        <p>Copy the long string that starts with <code>EAAB...</code>.</p>
        <Tip>By default this token expires in ~60 days. For production, follow Meta's "Generate a long-lived Page Access Token" guide to make it permanent.</Tip>
      </Step>

      <Step n="8" title="(WhatsApp only) Grab Phone Number ID + Permanent Token">
        <p>WhatsApp product → <strong className="text-[#F5F2EB]">API Setup</strong>. Copy these two values:</p>
        <ul className="list-none space-y-1 pl-1">
          <Check><strong className="text-[#F5F2EB]">Phone number ID</strong> (e.g. <code>123456789012345</code>)</Check>
          <Check><strong className="text-[#F5F2EB]">Access token</strong> — for production you need a System User Permanent Token (Meta docs explain it)</Check>
        </ul>
      </Step>

      <Step n="9" title="Paste tokens into AgentReply">
        <p>Back in AgentReply → <strong className="text-[#F5F2EB]">Meta channels</strong> tab → click the channel you're connecting:</p>
        <ul className="list-none space-y-1 pl-1">
          <Check><strong className="text-[#F5F2EB]">Messenger</strong>: paste App Secret + Page Access Token → Save</Check>
          <Check><strong className="text-[#F5F2EB]">Instagram</strong>: paste the IG-linked Page Access Token → Save</Check>
          <Check><strong className="text-[#F5F2EB]">WhatsApp Cloud</strong>: paste Phone Number ID + Permanent Token → Save</Check>
        </ul>
        <p>The status dot turns green ✓ when we successfully reach the Graph API with your credentials.</p>
      </Step>

      <Step n="10" title="Send a test message">
        <p>From a real Facebook/Instagram/WhatsApp account, message your business profile.</p>
        <p>Your AgentReply agent replies within a few seconds. The conversation appears in the <strong className="text-[#F5F2EB]">Contacts</strong> tab with a <em>channel</em> badge showing where it came from.</p>
        <Tip>Stuck? Check Meta App Dashboard → Webhooks → "Recent Deliveries". Each row shows the response code — look for non-200s and the error details.</Tip>
      </Step>

      <Step n="11" title="App Review (going to production)">
        <p>While your Meta App is in <strong className="text-[#F5F2EB]">Development mode</strong>, only Page admins can message and get a reply.</p>
        <p>To open it to the public, submit for <strong className="text-[#F5F2EB]">App Review</strong> requesting the <code>pages_messaging</code>, <code>instagram_basic</code>, <code>instagram_manage_messages</code>, and/or <code>whatsapp_business_messaging</code> permissions. Approval usually takes 1–3 business days.</p>
      </Step>
    </ArticleLayout>
  );
}
